/**
 * Reaction matrix — status-on-target × incoming damage school → reaction VFX.
 *
 * The interaction layer of the FX grammar (wave-2 spec): when a hit of school
 * S lands on a target carrying status T, a REACTION beat plays on top of the
 * normal hit — frozen targets SHATTER under physical hits, burn steams under
 * frost, poison ignites under fire. Purely presentational: damage numbers and
 * mechanics are untouched (combat lane owns any future mechanical reactions;
 * per the visual/damage decoupling doctrine this layer must stay honest —
 * a reaction never implies bonus damage that didn't happen).
 *
 * Data-driven so content/balance can grow the matrix without code: key is
 * `${statusId}+${school}`. Reaction effect ids live in ReactionVFX.ts,
 * composed from the burst grammar. Consumed by CombatEventVFXHandler after
 * the hit resolution; every trigger records a `reaction_vfx` lifecycle event
 * so QA reads reactions from JSON like everything else.
 */

import type { MarkFamily } from '../../game/effects/decals/decalBuilders';

export interface ReactionSpec {
  /** Registered VFXEffectConfig id (ReactionVFX.ts). */
  effectId: string;
  /** One-line read for docs/QA output. */
  note: string;
  /**
   * Optional PASSIVE aftermath decal (ground-marks spec §2.4): when the
   * reaction triggers, the emit site also drops a ground mark via the
   * DecalLayer — shatter leaves ice litter, wildfire scorches the ground.
   * Purely cosmetic (spec §3 doctrine carries over verbatim: a mark never
   * implies area damage that isn't happening).
   */
  aftermath?: { family: MarkFamily; school: string; size?: number; ttlMs?: number };
}

export const REACTION_MATRIX: Record<string, ReactionSpec> = {
  'frozen+physical': { effectId: 'react_shatter',  note: 'ice shell shatters under the blow',
                       aftermath: { family: 'frost', school: 'frost', size: 48 } },
  'frozen+fire':     { effectId: 'react_steam',    note: 'flash-thaw — steam veil rises',
                       aftermath: { family: 'pool', school: 'frost', size: 40 } },
  'burn+frost':      { effectId: 'react_quench',   note: 'flames quenched — hiss of steam' },
  'burn+nature':     { effectId: 'react_wildfire', note: 'flames catch the growth — wildfire flare',
                       aftermath: { family: 'scorch', school: 'fire', size: 56 } },
  'poison+fire':     { effectId: 'react_ignite',   note: 'toxins ignite — sickly flash',
                       aftermath: { family: 'scorch', school: 'fire', size: 48 } },
  'slow+nature':     { effectId: 'react_overgrow', note: 'the mire overgrows — vines seize the slowed target' },
  // ── Wave-3 growth (reaction-matrix-expansion spec §1) ─────────────────────
  'frozen+arcane':   { effectId: 'react_resonance',     note: 'the ice rings like struck crystal' },
  'frozen+holy':     { effectId: 'react_prism',         note: 'light refracts through the frozen shell' },
  'burn+holy':       { effectId: 'react_scour',         note: 'holy scours the flame to white ash' },
  'poison+holy':     { effectId: 'react_cleanse_flare', note: 'purge flash — the toxin leaves' },
  'poison+shadow':   { effectId: 'react_blight',        note: 'the toxin curdles — low, wet, ugly',
                       aftermath: { family: 'stain', school: 'shadow', size: 50 } },
  'corruption+holy': { effectId: 'react_repentance',    note: 'the rot recoils from the light',
                       aftermath: { family: 'sigil', school: 'holy', size: 44 } },
  'agony+physical':  { effectId: 'react_torment_spike', note: 'the wracked body jolts under the blow' },
  'unstable_affliction+arcane': { effectId: 'react_destabilize', note: 'the affliction detonates off-key' },
  // CoE amplifies MAGIC (canon-honest): one look, five lines — per-school
  // glint tint baked into generated near-clone defs (no playById tint seam yet).
  'curse_of_elements+fire':   { effectId: 'react_curse_amplify_fire',   note: 'the curse flares — fire amplified' },
  'curse_of_elements+frost':  { effectId: 'react_curse_amplify_frost',  note: 'the curse flares — frost amplified' },
  'curse_of_elements+arcane': { effectId: 'react_curse_amplify_arcane', note: 'the curse flares — arcane amplified' },
  'curse_of_elements+shadow': { effectId: 'react_curse_amplify_shadow', note: 'the curse flares — shadow amplified' },
  'curse_of_elements+nature': { effectId: 'react_curse_amplify_nature', note: 'the curse flares — nature amplified' },
  // immolate IS a burn (statusAura.config aliases its aura to status_burn) —
  // zero-def alias row keeps the quench fiction consistent (spec row 11).
  'immolate+frost':  { effectId: 'react_quench',        note: 'immolation quenched — hiss of steam' },
};

/**
 * School naming is unified at the matrix boundary. Matrix KEYS use canonical
 * content schools only (guarded by reactionMatrix.test.ts — the
 * `slow+lightning` dead-pair class is unrepresentable): no content ability
 * carries school 'lightning' (2026-07-17 census — shaman storm kit is school
 * 'nature'), so `lightning` aliases to `nature` and a lightning damageType
 * ever arriving on the wire still lands the slow+nature overgrow beat.
 */
export const SCHOOL_ALIASES: Record<string, string> = { ice: 'frost', lightning: 'nature' };

export function resolveReaction(statusIds: string[], school: string): ReactionSpec | null {
  const s = SCHOOL_ALIASES[school] ?? school;
  for (const status of statusIds) {
    const hit = REACTION_MATRIX[`${status}+${s}`];
    if (hit) return hit;
  }
  return null;
}
